// ============================================
// ContextPanel
// Context Layer configuration UI — visualize and modify
// the 17 context variables that change AI Nurse conversation tone
// ============================================

import { useState, useMemo } from 'react';
import { Sliders, RotateCcw, Shuffle, Info } from 'lucide-react';

// ── Types ──

type ContextKey = keyof typeof DEFAULT_CONTEXT;

interface ContextVariable {
  key: ContextKey;
  label: string;
  options: { value: string; label: string }[];
}

interface CategoryDef {
  name: string;
  color: string;
  borderColor: string;
  bgColor: string;
  variables: ContextVariable[];
}

// ── Default Context ──

const DEFAULT_CONTEXT = {
  timeOfDay: 'morning',
  cyclePhase: 'not_applicable',
  season: 'spring',
  timeSinceLastInteraction: 'first_visit',
  baselineStress: 'moderate',
  recentLifeEvent: 'none',
  motivation: 'curious',
  partnerStatus: 'partnered',
  caregiverBurden: 'none',
  socialSupport: 'moderate',
  budgetSensitivity: 'moderate',
  insuranceContext: 'insured',
  healthKnowledge: 'basic',
  supplementExperience: 'none',
  aiComfort: 'cautious',
  journeyStage: 'first_visit',
  adherenceLevel: 'unknown',
} as const;

type ContextState = { [K in ContextKey]: string };

// ── Category Definitions ──

const CATEGORIES: CategoryDef[] = [
  {
    name: 'Temporal',
    color: 'text-blue-400',
    borderColor: 'border-blue-400/30',
    bgColor: 'bg-blue-400/10',
    variables: [
      {
        key: 'timeOfDay',
        label: 'Time of Day',
        options: [
          { value: 'early_morning', label: 'Early Morning (5-8am)' },
          { value: 'morning', label: 'Morning (8am-12pm)' },
          { value: 'afternoon', label: 'Afternoon (12-5pm)' },
          { value: 'evening', label: 'Evening (5-9pm)' },
          { value: 'late_night', label: 'Late Night (9pm-5am)' },
        ],
      },
      {
        key: 'cyclePhase',
        label: 'Cycle Phase',
        options: [
          { value: 'not_applicable', label: 'Not Applicable' },
          { value: 'follicular', label: 'Follicular' },
          { value: 'ovulatory', label: 'Ovulatory' },
          { value: 'luteal', label: 'Luteal' },
          { value: 'menstrual', label: 'Menstrual' },
        ],
      },
      {
        key: 'season',
        label: 'Season',
        options: [
          { value: 'spring', label: 'Spring' },
          { value: 'summer', label: 'Summer' },
          { value: 'fall', label: 'Fall' },
          { value: 'winter', label: 'Winter' },
        ],
      },
      {
        key: 'timeSinceLastInteraction',
        label: 'Time Since Last Interaction',
        options: [
          { value: 'first_visit', label: 'First Visit' },
          { value: 'same_day', label: 'Same Day' },
          { value: 'days', label: 'Days' },
          { value: 'weeks', label: 'Weeks' },
          { value: 'months', label: 'Months' },
        ],
      },
    ],
  },
  {
    name: 'Emotional',
    color: 'text-pink-400',
    borderColor: 'border-pink-400/30',
    bgColor: 'bg-pink-400/10',
    variables: [
      {
        key: 'baselineStress',
        label: 'Baseline Stress',
        options: [
          { value: 'low', label: 'Low' },
          { value: 'moderate', label: 'Moderate' },
          { value: 'high', label: 'High' },
          { value: 'crisis', label: 'Crisis' },
        ],
      },
      {
        key: 'recentLifeEvent',
        label: 'Recent Life Event',
        options: [
          { value: 'none', label: 'None' },
          { value: 'positive', label: 'Positive (wedding, promotion)' },
          { value: 'negative', label: 'Negative (loss, job change)' },
          { value: 'health_scare', label: 'Health Scare' },
          { value: 'pregnancy', label: 'Pregnancy / Trying' },
        ],
      },
      {
        key: 'motivation',
        label: 'Motivation',
        options: [
          { value: 'eager', label: 'Eager' },
          { value: 'curious', label: 'Curious' },
          { value: 'skeptical', label: 'Skeptical' },
          { value: 'reluctant', label: 'Reluctant' },
          { value: 'desperate', label: 'Desperate' },
        ],
      },
    ],
  },
  {
    name: 'Relational',
    color: 'text-emerald-400',
    borderColor: 'border-emerald-400/30',
    bgColor: 'bg-emerald-400/10',
    variables: [
      {
        key: 'partnerStatus',
        label: 'Partner Status',
        options: [
          { value: 'single', label: 'Single' },
          { value: 'partnered', label: 'Partnered' },
          { value: 'divorced', label: 'Divorced / Separated' },
          { value: 'widowed', label: 'Widowed' },
        ],
      },
      {
        key: 'caregiverBurden',
        label: 'Caregiver Burden',
        options: [
          { value: 'none', label: 'None' },
          { value: 'children', label: 'Children' },
          { value: 'elderly_parent', label: 'Elderly Parent' },
          { value: 'both', label: 'Both' },
        ],
      },
      {
        key: 'socialSupport',
        label: 'Social Support',
        options: [
          { value: 'strong', label: 'Strong' },
          { value: 'moderate', label: 'Moderate' },
          { value: 'weak', label: 'Weak' },
          { value: 'isolated', label: 'Isolated' },
        ],
      },
    ],
  },
  {
    name: 'Financial',
    color: 'text-amber-400',
    borderColor: 'border-amber-400/30',
    bgColor: 'bg-amber-400/10',
    variables: [
      {
        key: 'budgetSensitivity',
        label: 'Budget Sensitivity',
        options: [
          { value: 'none', label: 'None (money no object)' },
          { value: 'low', label: 'Low' },
          { value: 'moderate', label: 'Moderate' },
          { value: 'high', label: 'High' },
          { value: 'severe', label: 'Severe' },
        ],
      },
      {
        key: 'insuranceContext',
        label: 'Insurance Context',
        options: [
          { value: 'insured', label: 'Insured' },
          { value: 'uninsured', label: 'Uninsured' },
          { value: 'underinsured', label: 'Underinsured' },
          { value: 'hsa_fsa', label: 'HSA/FSA Available' },
        ],
      },
    ],
  },
  {
    name: 'Literacy',
    color: 'text-purple-400',
    borderColor: 'border-purple-400/30',
    bgColor: 'bg-purple-400/10',
    variables: [
      {
        key: 'healthKnowledge',
        label: 'Health Knowledge',
        options: [
          { value: 'minimal', label: 'Minimal' },
          { value: 'basic', label: 'Basic' },
          { value: 'intermediate', label: 'Intermediate' },
          { value: 'advanced', label: 'Advanced' },
          { value: 'expert', label: 'Expert (clinician-level)' },
        ],
      },
      {
        key: 'supplementExperience',
        label: 'Supplement Experience',
        options: [
          { value: 'none', label: 'None' },
          { value: 'basic', label: 'Basic (multivitamin)' },
          { value: 'moderate', label: 'Moderate (targeted supps)' },
          { value: 'advanced', label: 'Advanced (stacks, cycling)' },
        ],
      },
      {
        key: 'aiComfort',
        label: 'AI Comfort',
        options: [
          { value: 'enthusiastic', label: 'Enthusiastic' },
          { value: 'comfortable', label: 'Comfortable' },
          { value: 'cautious', label: 'Cautious' },
          { value: 'distrustful', label: 'Distrustful' },
        ],
      },
    ],
  },
  {
    name: 'Clinical',
    color: 'text-red-400',
    borderColor: 'border-red-400/30',
    bgColor: 'bg-red-400/10',
    variables: [
      {
        key: 'journeyStage',
        label: 'Journey Stage',
        options: [
          { value: 'first_visit', label: 'First Visit' },
          { value: 'onboarding', label: 'Onboarding' },
          { value: 'active_protocol', label: 'Active Protocol' },
          { value: 'retest', label: 'Retest / Follow-Up' },
          { value: 'gate2', label: 'Gate 2 (Telehealth)' },
          { value: 'maintenance', label: 'Maintenance' },
          { value: 'lapsed', label: 'Lapsed' },
        ],
      },
      {
        key: 'adherenceLevel',
        label: 'Adherence Level',
        options: [
          { value: 'unknown', label: 'Unknown' },
          { value: 'excellent', label: 'Excellent (>90%)' },
          { value: 'good', label: 'Good (70-90%)' },
          { value: 'fair', label: 'Fair (50-70%)' },
          { value: 'poor', label: 'Poor (<50%)' },
        ],
      },
    ],
  },
];

// ── Mode Definitions ──

interface ModeDef {
  key: string;
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  compute: (ctx: ContextState) => boolean;
}

const MODES: ModeDef[] = [
  {
    key: 'high_empathy',
    label: 'High Empathy',
    color: 'text-pink-400',
    bgColor: 'bg-pink-400/10',
    borderColor: 'border-pink-400/30',
    compute: (ctx) =>
      ctx.baselineStress === 'high' ||
      ctx.baselineStress === 'crisis' ||
      ctx.recentLifeEvent !== 'none' ||
      ctx.timeOfDay === 'late_night',
  },
  {
    key: 'simplified',
    label: 'Simplified',
    color: 'text-blue-400',
    bgColor: 'bg-blue-400/10',
    borderColor: 'border-blue-400/30',
    compute: (ctx) =>
      (ctx.healthKnowledge === 'minimal' || ctx.healthKnowledge === 'basic') &&
      ctx.supplementExperience === 'none',
  },
  {
    key: 'evidence',
    label: 'Evidence-Heavy',
    color: 'text-purple-400',
    bgColor: 'bg-purple-400/10',
    borderColor: 'border-purple-400/30',
    compute: (ctx) =>
      ctx.healthKnowledge === 'expert' ||
      ctx.motivation === 'skeptical' ||
      ctx.aiComfort === 'distrustful',
  },
  {
    key: 'budget_conscious',
    label: 'Budget Conscious',
    color: 'text-amber-400',
    bgColor: 'bg-amber-400/10',
    borderColor: 'border-amber-400/30',
    compute: (ctx) =>
      ctx.budgetSensitivity === 'high' || ctx.budgetSensitivity === 'severe',
  },
  {
    key: 'continuity',
    label: 'Continuity',
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-400/10',
    borderColor: 'border-emerald-400/30',
    compute: (ctx) => ctx.journeyStage !== 'first_visit',
  },
];

// ── Helpers ──

function randomChoice<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

// ── Component ──

export function ContextPanel() {
  const [context, setContext] = useState<ContextState>({ ...DEFAULT_CONTEXT });

  function updateField(key: ContextKey, value: string) {
    setContext((prev) => ({ ...prev, [key]: value }));
  }

  function resetContext() {
    setContext({ ...DEFAULT_CONTEXT });
  }

  function randomizeContext() {
    const randomized: Partial<ContextState> = {};
    for (const cat of CATEGORIES) {
      for (const v of cat.variables) {
        randomized[v.key] = randomChoice(v.options).value;
      }
    }
    setContext(randomized as ContextState);
  }

  const activeModes = useMemo(() => {
    return MODES.filter((m) => m.compute(context));
  }, [context]);

  const changedCount = useMemo(() => {
    let count = 0;
    for (const key of Object.keys(DEFAULT_CONTEXT) as ContextKey[]) {
      if (context[key] !== DEFAULT_CONTEXT[key]) count++;
    }
    return count;
  }, [context]);

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-display text-gold flex items-center gap-2">
            <Sliders size={20} />
            Context Layer
          </h3>
          <p className="text-base text-text-secondary mt-1">
            Environmental modifiers that change AI Nurse conversation tone — never clinical output
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={randomizeContext}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-bg-elevated border border-bg-tertiary text-text-secondary hover:text-text-primary hover:border-gold/30 transition-colors text-base"
          >
            <Shuffle size={16} />
            Randomize
          </button>
          <button
            onClick={resetContext}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-bg-elevated border border-bg-tertiary text-text-secondary hover:text-text-primary hover:border-gold/30 transition-colors text-base"
          >
            <RotateCcw size={16} />
            Reset
          </button>
        </div>
      </div>

      {/* ── Changed indicator ── */}
      {changedCount > 0 && (
        <div className="text-base text-text-muted">
          {changedCount} of 17 variables changed from defaults
        </div>
      )}

      {/* ── Category Cards ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {CATEGORIES.map((cat) => (
          <div
            key={cat.name}
            className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden"
          >
            {/* Category header */}
            <div className="px-5 py-3 border-b border-bg-tertiary bg-bg-elevated/50 flex items-center gap-2">
              <span
                className={`text-base font-semibold font-display ${cat.color}`}
              >
                {cat.name}
              </span>
              <span className="text-sm text-text-muted">
                ({cat.variables.length} var{cat.variables.length !== 1 ? 's' : ''})
              </span>
            </div>

            {/* Variables */}
            <div className="px-5 py-4 space-y-4">
              {cat.variables.map((v) => (
                <div key={v.key}>
                  <label className="block text-base text-text-secondary mb-1.5">
                    {v.label}
                  </label>
                  <select
                    value={context[v.key]}
                    onChange={(e) => updateField(v.key, e.target.value)}
                    className="w-full bg-bg-elevated border border-bg-tertiary rounded-lg px-3 py-2.5 text-base text-text-primary focus:outline-none focus:border-gold/50 transition-colors appearance-none cursor-pointer"
                  >
                    {v.options.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* ── Active Modes ── */}
      <div
        className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden"
      >
        <div className="px-5 py-3 border-b border-bg-tertiary bg-bg-elevated/50 flex items-center gap-2">
          <Info size={16} className="text-gold" />
          <span className="text-base font-semibold font-display text-gold">
            Active Modes
          </span>
          <span className="text-sm text-text-muted ml-2">
            Computed from current context
          </span>
        </div>

        <div className="px-5 py-4">
          {activeModes.length === 0 ? (
            <p className="text-base text-text-muted">
              No special modes active — standard conversation tone will be used.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {activeModes.map((mode) => (
                <span
                  key={mode.key}
                  className={`text-base font-semibold px-3 py-1.5 rounded-lg border ${mode.color} ${mode.bgColor} ${mode.borderColor}`}
                >
                  {mode.label}
                </span>
              ))}
            </div>
          )}

          {/* All modes reference */}
          <div className="mt-4 pt-4 border-t border-bg-tertiary">
            <p className="text-sm text-text-muted mb-2">All available modes:</p>
            <div className="flex flex-wrap gap-2">
              {MODES.map((mode) => {
                const isActive = activeModes.some((m) => m.key === mode.key);
                return (
                  <span
                    key={mode.key}
                    className={`text-sm px-2.5 py-1 rounded-lg border transition-opacity ${
                      isActive
                        ? `${mode.color} ${mode.bgColor} ${mode.borderColor}`
                        : 'text-text-muted bg-bg-elevated border-bg-tertiary opacity-40'
                    }`}
                  >
                    {mode.label}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
